// src/components/admin/ClientListAdmin.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { AdminShell } from './Sidebar';
import Modal, { ModalButton } from '../ui/Modal';
import {
  StatCard, RefreshButton, FilterPill, SearchField,
  TableCard, LoadingState, EmptyState, Pagination, BulkBar,
} from '../ui/CrmPage';
import {
  ContractsListAdminPageRoute,
  CreateClientAdminPageRoute,
  EditClientAdminPageRoute,
} from '../../consts';

const fmtCurrency = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return new Intl.NumberFormat('ru-KZ', { maximumFractionDigits: 2 }).format(n) + ' ₸';
};
const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString('ru-RU');
};
const initials = (name) =>
  (name || '').split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('') || 'C';

function desc(a, b, getVal) {
  const av = getVal(a); const bv = getVal(b);
  if (bv == null && av != null) return -1;
  if (av == null && bv != null) return 1;
  if (bv < av) return -1;
  if (bv > av) return 1;
  return 0;
}
const getComparator = (order, getter) =>
  order === 'desc' ? (a, b) => desc(a, b, getter) : (a, b) => -desc(a, b, getter);
const stableSort = (arr, comp) =>
  arr.map((el, i) => [el, i]).sort((a, b) => { const o = comp(a[0], b[0]); return o || a[1] - b[1]; }).map((el) => el[0]);

const sortIcon = (active, dir) => (
  <span className={`crmpage__sort ${active ? 'is-active' : ''}`} aria-hidden>
    {active ? (dir === 'asc' ? '▲' : '▼') : '↕'}
  </span>
);

export default function ClientListAdmin() {
  const navigate = useNavigate();

  const apiBase = useMemo(() => {
    const base = process.env.REACT_APP_API_URL || '';
    return base.endsWith('/') ? base : `${base}/`;
  }, []);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const [q, setQ] = useState('');
  const [contactFilter, setContactFilter] = useState('all'); // 'all' | 'phone' | 'email' | 'none'
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');

  const [page, setPage] = useState(1);
  const rowsPerPage = 15;

  const [selected, setSelected] = useState([]);

  const [contractsOpen, setContractsOpen] = useState(false);
  const [contractsLoading, setContractsLoading] = useState(false);
  const [contractsError, setContractsError] = useState('');
  const [contracts, setContracts] = useState([]);
  const [activeClient, setActiveClient] = useState(null);

  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      setLoading(true); setError('');
      try {
        const r = await fetch(`${apiBase}v1/clients`, { signal: ctrl.signal });
        if (!r.ok) {
          const t = await r.text().catch(() => '');
          throw new Error(`HTTP ${r.status} ${r.statusText} ${t}`.trim());
        }
        const j = await r.json();
        setRows(Array.isArray(j) ? j : []);
        setSelected([]);
      } catch (e) {
        if (e.name !== 'AbortError') setError(e.message || 'Не удалось загрузить клиентов');
      } finally {
        setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [apiBase, reloadKey]);

  const totals = useMemo(() => {
    const total = rows.length;
    const withPhone = rows.filter((r) => !!r.phone).length;
    const withEmail = rows.filter((r) => !!r.email).length;
    const noContacts = rows.filter((r) => !r.phone && !r.email).length;
    return { total, withPhone, withEmail, noContacts };
  }, [rows]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return rows.filter((c) => {
      if (contactFilter === 'phone' && !c.phone) return false;
      if (contactFilter === 'email' && !c.email) return false;
      if (contactFilter === 'none' && (c.phone || c.email)) return false;
      if (!term) return true;
      const name = (c.name || '').toLowerCase();
      const bin = (c.bin || '').toLowerCase();
      const phone = (c.phone || '').toLowerCase();
      const addr = (c.address || '').toLowerCase();
      const email = (c.email || '').toLowerCase();
      return [name, bin, phone, addr, email].some((v) => v.includes(term));
    });
  }, [rows, q, contactFilter]);

  const sortGetter = useMemo(() => {
    switch (orderBy) {
      case 'name':    return (r) => (r.name || '').toLowerCase();
      case 'bin':     return (r) => (r.bin || '').toLowerCase();
      case 'phone':   return (r) => (r.phone || '').toLowerCase();
      case 'address': return (r) => (r.address || '').toLowerCase();
      case 'email':   return (r) => (r.email || '').toLowerCase();
      default:        return (r) => r[orderBy] ?? '';
    }
  }, [orderBy]);

  const sorted = useMemo(
    () => stableSort(filtered, getComparator(order, sortGetter)),
    [filtered, order, sortGetter]
  );
  const totalPages = Math.max(1, Math.ceil(sorted.length / rowsPerPage));
  const paged = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page]);
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const requestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const toggleSelect = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const toggleSelectAll = () => {
    const ids = paged.map((r) => r.id);
    const allSelected = ids.every((id) => selected.includes(id));
    setSelected(allSelected ? selected.filter((id) => !ids.includes(id)) : Array.from(new Set([...selected, ...ids])));
  };

  const handleDeleteSelected = async () => {
    if (selected.length === 0) return;
    try {
      await Promise.all(selected.map((id) => fetch(`${apiBase}v1/clients/${id}`, { method: 'DELETE' })));
      setReloadKey((k) => k + 1);
    } catch (e) {
      setError(e.message || 'Не удалось удалить клиентов');
    }
  };

  const handleSync = async () => {
    try {
      const r = await fetch(`${apiBase}v1/sync`, { method: 'POST' });
      if (!r.ok) {
        const t = await r.text().catch(() => '');
        throw new Error(`HTTP ${r.status} ${t}`.trim());
      }
      setReloadKey((k) => k + 1);
    } catch (e) {
      setError(e.message || 'Ошибка синхронизации');
    }
  };

  const hasFilters = q || contactFilter !== 'all';
  const resetFilters = () => { setQ(''); setContactFilter('all'); setPage(1); };

  const openContractsModal = async (client) => {
    setActiveClient(client);
    setContractsOpen(true);
    setContracts([]);
    setContractsError('');
    setContractsLoading(true);
    try {
      const res = await fetch(`${apiBase}v1/client_contracts/${client.id}`);
      if (res.status === 404) {
        setContracts([]);
      } else if (!res.ok) {
        const t = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status} ${t}`.trim());
      } else {
        const arr = await res.json();
        const list = Array.isArray(arr)
          ? [...arr].sort((a, b) => {
              const at = new Date(a.contract_date || 0).getTime() || 0;
              const bt = new Date(b.contract_date || 0).getTime() || 0;
              return bt - at;
            })
          : [];
        setContracts(list);
      }
    } catch (e) {
      setContractsError(e.message || 'Не удалось загрузить договоры клиента');
    } finally {
      setContractsLoading(false);
    }
  };

  const closeContractsModal = () => {
    setContractsOpen(false);
    setActiveClient(null);
    setContracts([]);
    setContractsError('');
  };

  const goToContract = (contractId) => {
    navigate(`${ContractsListAdminPageRoute}?open=${encodeURIComponent(String(contractId))}`);
    closeContractsModal();
  };

  return (
    <AdminShell
      breadcrumbs="Клиенты и договоры"
      title="Клиенты"
      subtitle="Список организаций и контактов"
      actions={
        <>
          <button type="button" className="adminbtn" onClick={handleSync}>
            <SyncIcon sx={{ fontSize: 18 }} />
            Синхронизировать
          </button>
          <button
            type="button"
            className="adminbtn adminbtn--primary"
            onClick={() => navigate(CreateClientAdminPageRoute)}
          >
            <AddIcon sx={{ fontSize: 18 }} />
            Добавить
          </button>
        </>
      }
    >
      <div className="crmpage crmpage--embedded">
        <div className="crmpage__main">
          <section className="crmpage__stats">
            <StatCard label="Всего"          value={totals.total}      accent="neutral" />
            <StatCard label="С телефоном"    value={totals.withPhone}  accent="success" />
            <StatCard label="С e-mail"       value={totals.withEmail}  accent="brand" />
            <StatCard label="Без контактов"  value={totals.noContacts} accent="neutral" />
            <RefreshButton onClick={() => setReloadKey((k) => k + 1)} />
          </section>

          <section className="crmpage__filters">
            <SearchField
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="Поиск: наименование, БИН, телефон, адрес, e-mail…"
            />
            <div className="crmpage__pills">
              <FilterPill active={contactFilter === 'all'}   onClick={() => { setContactFilter('all'); setPage(1); }}>Все</FilterPill>
              <FilterPill active={contactFilter === 'phone'} onClick={() => { setContactFilter('phone'); setPage(1); }}>С телефоном</FilterPill>
              <FilterPill active={contactFilter === 'email'} onClick={() => { setContactFilter('email'); setPage(1); }}>С e-mail</FilterPill>
              <FilterPill active={contactFilter === 'none'}  onClick={() => { setContactFilter('none'); setPage(1); }}>Без контактов</FilterPill>
            </div>
            {hasFilters && (
              <button type="button" className="crmpage__reset" onClick={resetFilters}>Сбросить</button>
            )}
          </section>

          {error && <div className="crmpage__alert crmpage__alert--error">{error}</div>}

          <TableCard>
            {loading ? (
              <LoadingState text="Загружаем клиентов…" />
            ) : paged.length === 0 ? (
              <EmptyState
                title="Ничего не найдено"
                text={hasFilters ? 'Сбросьте фильтры или измените запрос.' : 'Здесь появятся клиенты.'}
              />
            ) : (
              <>
                <div className="crmpage__table-wrap">
                  <table className="crmpage__table">
                    <colgroup>
                      <col style={{ width: 40 }} />
                      <col />
                      <col style={{ width: 160 }} />
                      <col style={{ width: 160 }} />
                      <col />
                      <col style={{ width: 220 }} />
                      <col style={{ width: 100 }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="crmpage__th-check">
                          <input
                            type="checkbox"
                            className="crmpage__check"
                            checked={paged.length > 0 && paged.every((r) => selected.includes(r.id))}
                            onChange={toggleSelectAll}
                            aria-label="Выделить все"
                          />
                        </th>
                        <th onClick={() => requestSort('name')}    style={{ cursor: 'pointer' }}>Наименование {sortIcon(orderBy === 'name', order)}</th>
                        <th onClick={() => requestSort('bin')}     style={{ cursor: 'pointer' }}>БИН {sortIcon(orderBy === 'bin', order)}</th>
                        <th onClick={() => requestSort('phone')}   style={{ cursor: 'pointer' }}>Телефон {sortIcon(orderBy === 'phone', order)}</th>
                        <th onClick={() => requestSort('address')} style={{ cursor: 'pointer' }}>Адрес {sortIcon(orderBy === 'address', order)}</th>
                        <th onClick={() => requestSort('email')}   style={{ cursor: 'pointer' }}>E-mail {sortIcon(orderBy === 'email', order)}</th>
                        <th style={{ textAlign: 'right' }}>Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paged.map((c) => {
                        const checked = selected.includes(c.id);
                        return (
                          <tr
                            key={c.id}
                            className={`crmpage__row ${checked ? 'crmpage__row--selected' : ''}`}
                            onClick={() => openContractsModal(c)}
                          >
                            <td className="crmpage__td-check" onClick={(e) => { e.stopPropagation(); toggleSelect(c.id); }}>
                              <input
                                type="checkbox"
                                className="crmpage__check"
                                checked={checked}
                                onChange={() => toggleSelect(c.id)}
                              />
                            </td>
                            <td>
                              <div className="crmpage__user-cell">
                                <span className="crmpage__avatar" aria-hidden>{initials(c.name)}</span>
                                <div className="crmpage__primary">{c.name || '—'}</div>
                              </div>
                            </td>
                            <td className="crmpage__date">{c.bin || '—'}</td>
                            <td className="crmpage__muted">{c.phone || '—'}</td>
                            <td className="crmpage__muted crmpage__td--wrap">{c.address || '—'}</td>
                            <td className="crmpage__muted crmpage__td--wrap">{c.email || '—'}</td>
                            <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                              <div className="crmpage__row-actions">
                                <button
                                  type="button"
                                  className="adminbtn adminbtn--icon"
                                  title="Редактировать"
                                  onClick={() => navigate(EditClientAdminPageRoute.replace(':id', String(c.id)))}
                                ><EditIcon sx={{ fontSize: 18 }} /></button>
                                <button
                                  type="button"
                                  className="adminbtn adminbtn--icon"
                                  title="Договоры"
                                  onClick={() => openContractsModal(c)}
                                ><OpenInNewIcon sx={{ fontSize: 18 }} /></button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <Pagination current={page} total={totalPages} onChange={setPage} />
              </>
            )}
          </TableCard>

          <BulkBar count={selected.length} onClear={() => setSelected([])}>
            <button
              type="button"
              className="crmpage__btn crmpage__btn--danger"
              onClick={handleDeleteSelected}
            >
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
              Удалить выбранных
            </button>
          </BulkBar>
        </div>
      </div>

      <Modal
        isOpen={contractsOpen}
        onClose={closeContractsModal}
        title={activeClient?.name || '—'}
        subtitle={activeClient?.bin ? `БИН: ${activeClient.bin}` : 'Договоры клиента'}
        size="lg"
      >
        {contractsLoading ? (
          <LoadingState text="Загружаем договоры…" />
        ) : contractsError ? (
          <div className="crmpage__alert crmpage__alert--error">{contractsError}</div>
        ) : contracts.length === 0 ? (
          <EmptyState title="Договоров нет" text="У этого клиента нет зарегистрированных договоров." />
        ) : (
          <div className="crmpage__table-wrap" style={{ maxHeight: 520 }}>
            <table className="crmpage__table">
              <colgroup>
                <col />
                <col style={{ width: 140 }} />
                <col style={{ width: 160 }} />
                <col style={{ width: 110 }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Номер</th>
                  <th>Дата</th>
                  <th style={{ textAlign: 'right' }}>Остаток</th>
                  <th style={{ textAlign: 'right' }}>Действие</th>
                </tr>
              </thead>
              <tbody>
                {contracts.map((ct) => (
                  <tr key={ct.contract_id} className="crmpage__row" onClick={() => goToContract(ct.contract_id)}>
                    <td><span className="crmpage__num">№ {ct.contract_number || '—'}</span></td>
                    <td className="crmpage__date">{fmtDate(ct.contract_date)}</td>
                    <td className="crmpage__num" style={{ textAlign: 'right' }}>{fmtCurrency(ct.remaining_amount)}</td>
                    <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="adminbtn adminbtn--icon"
                        title="Открыть"
                        onClick={() => goToContract(ct.contract_id)}
                      ><OpenInNewIcon sx={{ fontSize: 18 }} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>
    </AdminShell>
  );
}
